import { zodResolver } from '@hookform/resolvers/zod'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Plus, UserPlus, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { apiErrorCode, apiErrorMessage } from '@/api/client'
import DateInput from '@/components/ui/DateInput'
import TimeInput from '@/components/ui/TimeInput'
import { useGridTime } from '@/hooks/useGridTime'
import { ChipGroup, courseChips, languageChips } from '@/components/ui/ChipGroup'
import { instructors as instructorsApi, rewards as rewardsApi } from '@/api/endpoints'
import { Alert, Checkbox, Field, Input, Modal, Select, Spinner, Textarea } from '@/components/ui'
import { addDays, cn, formatMoney, formatTime, toISODate, toISODateTime } from '@/lib/utils'
import { useAuth } from '@/store/auth'
import type { CalendarResponse, Instructor, InstructorReservation } from '@/types/api'

import { useSaveInstructorReservation, useSaveInstructorReservations } from './useCalendar'
import { rewardTypeLabel } from '@/features/rewards/typeLabel'
import { CenterSelect, useCenterFilter, useCenters } from '@/features/centers/useCenters'

/** Odmeny za auto a odvoz - ukazujú sa pri zaškrtnutí „vlastné auto“. */
const CAR_REWARDS = ['own-car', 'transporte-people']

const schema = z.object({
  instructor_id: z.number().nullable(),
  fname: z.string().min(1),
  sname: z.string().optional(),
  email: z.string().email().or(z.literal('')).optional(),
  tel: z.string().optional(),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  start: z.string().regex(/^\d{2}:\d{2}$/),
  end: z.string().regex(/^\d{2}:\d{2}$/),
  status_id: z.number(),
  language_ids: z.array(z.number()).min(1),
  course_ids: z.array(z.number()).min(1),
  owncar: z.boolean(),
  passengers_in_car: z.number().min(0),
  note: z.string().optional(),
})
type FormValues = z.infer<typeof schema>

interface Props {
  reservation: InstructorReservation | null
  day: string
  codebooks: CalendarResponse
  onClose: () => void
}

export default function InstructorReservationModal({
  reservation,
  day,
  codebooks,
  onClose,
}: Props) {
  const { t } = useTranslation()
  const save = useSaveInstructorReservation()
  // Časy po dielikoch kalendára (rozdelenie hodín v nastaveniach).
  const gridTime = useGridTime()
  const saveMany = useSaveInstructorReservations()
  const [error, setError] = useState<string | null>(null)
  // Stredisko bloku (`sql/040`) - pri novom bloku to, na ktoré je kalendár
  // práve zúžený (ak na jedno), inak hlavné.
  const [filteredCenters] = useCenterFilter()
  const { multi: multiCenter } = useCenters()
  const [centerId, setCenterId] = useState<number>(
    reservation?.center_id ?? (filteredCenters.length === 1 ? filteredCenters[0] : 1),
  )
  const [search, setSearch] = useState('')
  const [suggestions, setSuggestions] = useState<CalendarResponse['instructors']>([])
  /** Ďalšie termíny toho istého inštruktora — len pri zakladaní, viď `ClientReservationModal`. */
  const [extra, setExtra] = useState<Array<{ date: string; start: string; end: string }>>([])

  const { grid } = codebooks

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: reservation
      ? {
          instructor_id: reservation.instructor_id,
          fname: reservation.instructor?.fname ?? '',
          sname: reservation.instructor?.sname ?? '',
          email: reservation.instructor?.email ?? '',
          tel: reservation.instructor?.tel ?? '',
          date: reservation.start?.slice(0, 10) ?? day,
          start: formatTime(reservation.start),
          end: formatTime(reservation.end),
          status_id: reservation.status_id ?? 2,
          language_ids: reservation.language_ids.length ? reservation.language_ids : [1],
          course_ids: reservation.course_ids.length ? reservation.course_ids : [1],
          owncar: reservation.owncar,
          passengers_in_car: reservation.passengers_in_car,
          note: reservation.note ?? '',
        }
      : {
          instructor_id: null,
          fname: '',
          sname: '',
          email: '',
          tel: '',
          date: day,
          start: hourLabel(grid.start_hour),
          end: hourLabel(grid.end_hour),
          status_id: 2,
          language_ids: [1],
          course_ids: [1],
          owncar: false,
          passengers_in_car: 0,
          note: '',
        },
  })

  const owncar = watch('owncar')
  const selectedId = watch('instructor_id')
  const canWrite = Boolean(useAuth((s) => s.user)?.can_write)

  /** Odmena za auto a odvoz hneď po zaškrtnutí - orientačne, nič sa
   *  nezapisuje (`quote_block` v `rewards/views.py`). Vznikne až uložením. */
  const passengers = watch('passengers_in_car')
  const { data: carQuote } = useQuery({
    queryKey: ['reward-quote-block', reservation?.id, selectedId, watch('date'), watch('start'), watch('end'), watch('status_id'), owncar, passengers],
    queryFn: () =>
      rewardsApi.quoteBlock({
        id: reservation?.id ?? null,
        instructor_id: selectedId,
        start: toISODateTime(watch('date'), watch('start')),
        end: toISODateTime(watch('date'), watch('end')),
        status_id: watch('status_id'),
        owncar,
        passengers_in_car: Number.isNaN(passengers) ? 0 : passengers,
      }),
    enabled: canWrite && owncar && Boolean(selectedId),
    placeholderData: keepPreviousData,
  })
  const carRows = (carQuote?.rows ?? []).filter((row) => CAR_REWARDS.includes(row.type_name))
  const selectedName = [watch('fname'), watch('sname')].filter(Boolean).join(' ').trim()

  // Instructor autocomplete (replaces handleSearchInstructor).
  useEffect(() => {
    if (search.trim().length < 2) {
      setSuggestions([])
      return
    }
    const timer = setTimeout(() => {
      void instructorsApi
        .list({ q: search, limit: 8 })
        .then((res) => setSuggestions(res.results as CalendarResponse['instructors']))
        .catch(() => setSuggestions([]))
    }, 250)
    return () => clearTimeout(timer)
  }, [search])

  /** Fill the form from an existing instructor - the point of the side list. */
  function pick(instructor: Instructor) {
    setValue('instructor_id', instructor.id)
    setValue('fname', instructor.fname ?? '')
    setValue('sname', instructor.sname ?? '')
    setValue('email', instructor.email ?? '')
    setValue('tel', instructor.tel ?? '')
    if (instructor.language_ids?.length) setValue('language_ids', instructor.language_ids)
    if (instructor.course_ids?.length) setValue('course_ids', instructor.course_ids)
    setSuggestions([])
    setSearch('')
  }

  /** Back to "this is somebody new". */
  function clearPick() {
    setValue('instructor_id', null)
    setValue('fname', '')
    setValue('sname', '')
    setValue('email', '')
    setValue('tel', '')
  }

  /** Pridať ďalší termín — predvolene o deň neskôr, v ten istý čas. */
  function addTerm() {
    const last = extra[extra.length - 1]
    const base = last ?? { date: watch('date'), start: watch('start'), end: watch('end') }
    setExtra([
      ...extra,
      { date: toISODate(addDays(new Date(base.date), 1)), start: base.start, end: base.end },
    ])
  }

  function setTerm(index: number, patch: Partial<{ date: string; start: string; end: string }>) {
    setExtra(extra.map((term, i) => (i === index ? { ...term, ...patch } : term)))
  }

  async function onSubmit(values: FormValues) {
    setError(null)

    const payload = {
      id: reservation?.id ?? null,
      instructor_id: values.instructor_id,
      fname: values.fname,
      sname: values.sname ?? '',
      email: values.email || null,
      tel: values.tel || null,
      start: toISODateTime(values.date, values.start),
      end: toISODateTime(values.date, values.end),
      status_id: values.status_id,
      language_ids: values.language_ids,
      course_ids: values.course_ids,
      owncar: values.owncar,
      passengers_in_car: values.owncar ? values.passengers_in_car : 0,
      note: values.note ?? '',
      center_id: centerId,
    }

    try {
      if (!reservation && extra.length) {
        await saveMany.mutateAsync({
          ...payload,
          occurrences: extra.map((term) => ({
            start: toISODateTime(term.date, term.start),
            end: toISODateTime(term.date, term.end),
          })),
        })
      } else {
        await save.mutateAsync(payload)
      }
      onClose()
    } catch (err) {
      const code = apiErrorCode(err)
      setError(
        code === 'instructor.hasInDay'
          ? t('reservation.instructorHasInDay')
          : code === 'reservation.mixedCenters'
            ? t('centers.mixedCenters')
            : apiErrorMessage(err, t('errors.generic')),
      )
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title={
        reservation
          ? t('calendar.editInstructorReservation')
          : t('calendar.newInstructorReservation')
      }
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t('global.cancel')}
          </button>
          <button
            type="submit"
            form="instructor-reservation-form"
            className="btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? <Spinner /> : null}
            {t('global.save')}
          </button>
        </>
      }
    >
      {error ? (
        <div className="mb-3">
          <Alert variant="error">{error}</Alert>
        </div>
      ) : null}

      <div className="flex gap-3">
        <div className="min-w-0 flex-1 space-y-2.5">
          {/* Whether this block will attach to somebody who already exists or
              create a new instructor is the one thing that is easy to get
              wrong here, so it is stated outright. */}
          <div
            className={cn(
              'flex items-center gap-2 rounded border px-2 py-1 text-[12px]',
              selectedId
                ? 'border-green-300 bg-green-50 text-green-800'
                : 'border-amber-300 bg-amber-50 text-amber-800',
            )}
          >
            {selectedId ? (
              <>
                <span className="font-medium">{t('calendar.pickSelected')}:</span>
                <strong className="truncate">{selectedName || '—'}</strong>
                <button
                  type="button"
                  className="ml-auto inline-flex items-center gap-1 underline hover:no-underline"
                  onClick={clearPick}
                >
                  <X size={12} />
                  {t('calendar.pickClear')}
                </button>
              </>
            ) : (
              <>
                <UserPlus size={13} />
                <span className="font-medium">{t('calendar.pickNew')}</span>
                <span className="ml-auto text-amber-700/80">{selectedName}</span>
              </>
            )}
          </div>

          <form
            id="instructor-reservation-form"
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-2.5"
          >
            <div className="grid gap-2 sm:grid-cols-4">
              <Field label={t('reservation.date')} error={errors.date && t('errors.invalidDate')}>
                <DateInput
                  value={watch('date')}
                  onChange={(value) => setValue('date', value, { shouldDirty: true, shouldValidate: true })}
                />
              </Field>
              <Field label={t('reservation.start')} error={errors.start && t('errors.required')}>
                <TimeInput {...gridTime}
                  value={watch('start')}
                  onChange={(value) => setValue('start', value, { shouldDirty: true, shouldValidate: true })}
                />
              </Field>
              <Field label={t('reservation.end')} error={errors.end && t('errors.required')}>
                <TimeInput {...gridTime}
                  value={watch('end')}
                  onChange={(value) => setValue('end', value, { shouldDirty: true, shouldValidate: true })}
                />
              </Field>
              <Field label={t('reservation.status')}>
                <Select {...register('status_id', { valueAsNumber: true })}>
                  {codebooks.statuses.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name ?? s.key}
                    </option>
                  ))}
                </Select>
              </Field>
              {multiCenter ? (
                <Field label={t('centers.center')}>
                  <CenterSelect value={centerId} onChange={setCenterId} />
                </Field>
              ) : null}
            </div>

            {/* Ďalšie termíny — len pri zakladaní, rovnako ako pri klientovi. */}
            {!reservation ? (
              <div>
                {extra.map((term, index) => (
                  <div key={index} className="mb-2 grid items-end gap-2 sm:grid-cols-4">
                    <Field label={index === 0 ? t('reservation.moreTerms') : undefined}>
                      <DateInput value={term.date} onChange={(date) => setTerm(index, { date })} />
                    </Field>
                    <Field label={index === 0 ? t('reservation.start') : undefined}>
                      <TimeInput {...gridTime} value={term.start} onChange={(start) => setTerm(index, { start })} />
                    </Field>
                    <Field label={index === 0 ? t('reservation.end') : undefined}>
                      <TimeInput {...gridTime} value={term.end} onChange={(end) => setTerm(index, { end })} />
                    </Field>
                    <button
                      type="button"
                      className="btn-secondary h-[34px] w-fit px-2"
                      title={t('global.delete')}
                      onClick={() => setExtra(extra.filter((_, i) => i !== index))}
                    >
                      <X size={15} />
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-[13px] font-medium
                             text-brand-600 underline-offset-2 hover:underline"
                  onClick={addTerm}
                >
                  <Plus size={14} />
                  {t('reservation.addTerm')}
                </button>

                {extra.length ? (
                  <p className="mt-2 text-xs text-slate-500">
                    {t('reservation.moreTermsHintInstructor', { count: extra.length + 1 })}
                  </p>
                ) : null}
              </div>
            ) : null}

            <fieldset className="rounded border border-slate-200 p-2.5">
              <legend className="px-1 text-xs font-medium uppercase text-slate-500">
                {t('reservation.instructor')}
              </legend>

              <div className="grid gap-2 sm:grid-cols-4">
                <Field label={t('reservation.firstname')} error={errors.fname && t('errors.required')}>
                  <div className="relative">
                    <Input
                      {...register('fname')}
                      onChange={(e) => {
                        setValue('fname', e.target.value)
                        setSearch(e.target.value)
                      }}
                      autoComplete="off"
                    />
                    {suggestions.length > 0 ? (
                      <ul className="absolute z-10 mt-1 max-h-52 w-72 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg">
                        {suggestions.map((s) => (
                          <li key={s.id}>
                            <button
                              type="button"
                              className="block w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50"
                              onClick={() => pick(s as Instructor)}
                            >
                              {s.full_name}
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                </Field>
                <Field label={t('reservation.surname')}>
                  <Input {...register('sname')} />
                </Field>
                <Field label={t('reservation.email')} error={errors.email && t('errors.invalidEmail')}>
                  <Input type="email" {...register('email')} />
                </Field>
                <Field label={t('reservation.phone')}>
                  <Input {...register('tel')} />
                </Field>
              </div>
            </fieldset>

            <div className="grid gap-2 sm:grid-cols-2">
              <Controller
                control={control}
                name="language_ids"
                render={({ field }) => (
                  <Field
                    label={t('instructors.languages')}
                    error={errors.language_ids && t('errors.required')}
                  >
                    <ChipGroup
                      options={languageChips(codebooks.languages.filter((l) => l.use !== false))}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </Field>
                )}
              />
              <Controller
                control={control}
                name="course_ids"
                render={({ field }) => (
                  <Field
                    label={t('instructors.courses')}
                    error={errors.course_ids && t('errors.required')}
                  >
                    <ChipGroup
                      options={courseChips(codebooks.courses)}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </Field>
                )}
              />
            </div>

            <div className="flex flex-wrap items-end gap-4">
              <Controller
                control={control}
                name="owncar"
                render={({ field }) => (
                  <Checkbox
                    label={t('calendar.ownCar')}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                )}
              />
              {owncar ? (
                <Field label={t('calendar.passengers')} className="w-32">
                  <Input
                    type="number"
                    min={0}
                    max={8}
                    {...register('passengers_in_car', { valueAsNumber: true })}
                  />
                </Field>
              ) : null}
              {owncar && carQuote ? (
                <div className="pb-2 text-xs text-slate-600">
                  <span className="text-slate-500">{t('rewards.blockQuote')}: </span>
                  {carRows.length ? (
                    carRows.map((row, index) => (
                      <span key={row.type_name}>
                        {index ? ' · ' : ''}
                        {rewardTypeLabel(t, row)}
                        {row.type_name === 'transporte-people' ? ` (${t('rewards.persons', { count: row.number })})` : ''}{' '}
                        <span className="font-medium text-slate-800">{formatMoney(row.reward)}</span>
                      </span>
                    ))
                  ) : (
                    <span className="text-amber-700">{t('rewards.noneCar')}</span>
                  )}
                </div>
              ) : null}
            </div>

            <Field label={t('global.note')}>
              <Textarea {...register('note')} />
            </Field>
          </form>
        </div>

        <InstructorPicker selectedId={selectedId} onPick={pick} />
      </div>
    </Modal>
  )
}

type PickerSort = 'created' | 'count' | 'priority'

const PICKER_ORDERING: Record<PickerSort, string> = {
  created: '-created',
  count: '-reservations_count',
  priority: '-priority',
}

/**
 * The instructor list next to the form. It exists so the common case - "this
 * block is for somebody who already works here" - never needs typing.
 */
function InstructorPicker({
  selectedId,
  onPick,
}: {
  selectedId: number | null
  onPick: (instructor: Instructor) => void
}) {
  const { t } = useTranslation()
  const [sort, setSort] = useState<PickerSort>(
    () => (localStorage.getItem('l2s:pickerSort') as PickerSort) ?? 'count',
  )
  const [query, setQuery] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['instructors', 'picker', sort],
    queryFn: () => instructorsApi.list({ ordering: PICKER_ORDERING[sort], limit: 200 }),
    staleTime: 60_000,
  })

  function changeSort(value: PickerSort) {
    setSort(value)
    localStorage.setItem('l2s:pickerSort', value)
  }

  const needle = query.trim().toLowerCase()
  const rows = (data?.results ?? []).filter(
    (i) => !needle || (i.full_name ?? '').toLowerCase().includes(needle),
  )

  return (
    <aside className="hidden w-56 shrink-0 flex-col md:flex">
      <div className="text-[13px] font-medium">{t('calendar.pickInstructor')}</div>

      <div className="mt-1 text-[11px] text-slate-500">{t('calendar.pickSortBy')}</div>
      <div className="mb-1.5 flex flex-wrap items-center gap-x-1.5 text-[11px]">
        {(
          [
            ['created', t('calendar.pickSortCreated')],
            ['count', t('calendar.pickSortCount')],
            ['priority', t('calendar.pickSortPriority')],
          ] as Array<[PickerSort, string]>
        ).map(([value, label], index) => (
          <span key={value} className="flex items-center gap-1.5">
            {index > 0 ? <span className="text-slate-300">|</span> : null}
            <button
              type="button"
              onClick={() => changeSort(value)}
              className={cn(
                'hover:underline',
                sort === value ? 'font-semibold text-brand-700' : 'text-brand-500',
              )}
            >
              {label}
            </button>
          </span>
        ))}
      </div>

      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('calendar.pickSearch')}
        className="mb-1 h-7 text-[12px]"
      />

      <div className="max-h-72 flex-1 overflow-y-auto rounded border border-slate-200">
        {isLoading ? (
          <div className="flex h-16 items-center justify-center text-slate-400">
            <Spinner />
          </div>
        ) : rows.length === 0 ? (
          <div className="px-2 py-3 text-center text-[12px] text-slate-400">
            {t('calendar.pickEmpty')}
          </div>
        ) : (
          rows.map((instructor) => (
            <button
              key={instructor.id}
              type="button"
              onClick={() => onPick(instructor)}
              className={cn(
                'flex w-full items-baseline gap-1 border-b border-slate-100 px-2 py-1 text-left',
                'text-[12px] last:border-b-0 hover:bg-action-50',
                selectedId === instructor.id && 'bg-action-100 ring-1 ring-inset ring-action-400',
              )}
            >
              <span className="truncate">
                {instructor.fname}{' '}
                <strong className="font-semibold">{instructor.sname}</strong>
              </span>
              <span className="ml-auto shrink-0 text-[11px] text-slate-400">
                {instructor.reservations_count ?? 0}
              </span>
            </button>
          ))
        )}
      </div>
    </aside>
  )
}

function hourLabel(value: number): string {
  const h = Math.floor(value)
  const m = Math.round((value - h) * 60)
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}
